import { useEffect, useState } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import systemService from '../../services/systemService';
import { setSypPerUsd } from '../../config/store';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';
import Input from '../../components/Common/Input';
import { formatDateTime } from '../../utils/formatters';

const ExchangeRateSettings = () => {
  const { isRtl } = useLocalization();
  const [isLoading, setIsLoading] = useState(true);
  const [currentRate, setCurrentRate] = useState(null);
  const [updatedAtUtc, setUpdatedAtUtc] = useState(null);
  const [rateInput, setRateInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    loadRate();
  }, []);

  const loadRate = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await systemService.getExchangeRate();
      setCurrentRate(data.sypPerUsd);
      setUpdatedAtUtc(data.updatedAtUtc);
      setRateInput(String(data.sypPerUsd));
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || (isRtl ? 'تعذر تحميل سعر الصرف' : 'Failed to load exchange rate'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    const value = Number(rateInput);
    if (!Number.isFinite(value) || value <= 0) {
      setError(isRtl ? 'الرجاء إدخال سعر صرف صالح أكبر من صفر' : 'Please enter a valid exchange rate greater than 0');
      return;
    }

    setIsSubmitting(true);
    try {
      const data = await systemService.updateExchangeRate(value);
      setCurrentRate(data.sypPerUsd);
      setUpdatedAtUtc(data.updatedAtUtc);
      setSypPerUsd(data.sypPerUsd);
      setSuccessMessage(isRtl ? 'تم تحديث سعر الصرف بنجاح' : 'Exchange rate updated successfully');
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || (isRtl ? 'فشل تحديث سعر الصرف' : 'Failed to update exchange rate'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <Loading text={isRtl ? 'جاري تحميل سعر الصرف...' : 'Loading exchange rate...'} />;
  }

  const previewValue = Number(rateInput);
  const hasValidPreview = Number.isFinite(previewValue) && previewValue > 0;

  return (
    <div className="exchange-rate-content" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">{isRtl ? 'سعر الصرف' : 'Exchange Rate'}</h1>
            <p className="page-description">
              {isRtl
                ? 'حدد سعر صرف الدولار مقابل الليرة السورية. يُستخدم هذا السعر لعرض الأسعار بالدولار أو بالليرة في المتجر ولوحة التحكم.'
                : 'Set the USD to Syrian Lira exchange rate. This rate is used to display prices in dollars or lira across the storefront and admin panel.'}
            </p>
          </div>
        </div>
      </div>

      <div className="card card-full-width">
        <div className="card-header">
          <h2 className="card-title">{isRtl ? 'السعر الحالي' : 'Current Rate'}</h2>
        </div>
        <div className="card-body">
          <div className="exchange-rate-current">
            <span className="exchange-rate-current-value">
              1 $ = {currentRate != null ? currentRate.toLocaleString(isRtl ? 'ar-SA-u-nu-latn' : 'en-US') : '—'} {isRtl ? 'ل.س' : 'SYP'}
            </span>
            {updatedAtUtc && (
              <span className="exchange-rate-updated">
                {isRtl ? 'آخر تحديث: ' : 'Last updated: '}
                {formatDateTime(updatedAtUtc, isRtl ? 'ar' : 'en')}
              </span>
            )}
          </div>

          <form onSubmit={handleSubmit} className="exchange-rate-form">
            {error && (
              <div className="form-error" style={{ marginBottom: 'var(--sp-4)', padding: 'var(--sp-3)', background: 'var(--danger)', color: 'white', borderRadius: 'var(--r-md)' }}>
                {error}
              </div>
            )}
            {successMessage && (
              <div className="form-success" style={{ marginBottom: 'var(--sp-4)', padding: 'var(--sp-3)', background: 'var(--success)', color: 'white', borderRadius: 'var(--r-md)' }}>
                {successMessage}
              </div>
            )}

            <Input
              label={isRtl ? 'عدد الليرات السورية مقابل 1 دولار' : 'Syrian Lira per 1 USD'}
              type="number"
              value={rateInput}
              onChange={(e) => { setRateInput(e.target.value); setSuccessMessage(null); }}
              min={0}
              step="1"
              required
            />

            {hasValidPreview && (
              <div className="exchange-rate-preview">
                {isRtl
                  ? `مثال: منتج بسعر 10$ سيظهر بـ ${(previewValue * 10).toLocaleString('ar-SA-u-nu-latn')} ل.س`
                  : `Example: a $10 product will show as ${(previewValue * 10).toLocaleString('en-US')} SYP`}
              </div>
            )}

            <div className="exchange-rate-actions">
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? (isRtl ? 'جاري الحفظ...' : 'Saving...') : (isRtl ? 'حفظ' : 'Save')}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <style>{`
        .exchange-rate-current {
          display: flex;
          flex-direction: column;
          gap: var(--sp-1);
          padding-bottom: var(--sp-5);
          margin-bottom: var(--sp-5);
          border-bottom: 1px solid var(--border);
        }
        .exchange-rate-current-value {
          font-family: var(--font-display, inherit);
          font-size: var(--fs-2xl, 1.75rem);
          font-weight: 700;
          color: var(--text);
        }
        .exchange-rate-updated {
          font-size: var(--fs-sm);
          color: var(--text-muted);
        }
        .exchange-rate-form {
          max-width: 420px;
        }
        .exchange-rate-preview {
          font-size: var(--fs-sm);
          color: var(--text-muted);
          margin: -0.5rem 0 var(--sp-4);
        }
        .exchange-rate-actions {
          display: flex;
          justify-content: flex-end;
        }
      `}</style>
    </div>
  );
};

export default ExchangeRateSettings;
